import React, {
  FunctionComponent,
  useState,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import { Button, Card } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import cls from 'classnames';

import { ICourse } from "../../../../features/courses/coursesSlice";
import { ParentTypeEnum } from "../../../../features/evaluation/common/enums";
import { QuestionsAnswers } from "../../../../features/evaluation/common/interfaces";
import { isRegisteringEvaluationResult, selectEvaluation } from "../../../../features/evaluation/currentEvaluationSlice";
import { selectStudentCourse } from "../../../../features/students/courseSlice";
import { CogIcon } from "../../../common/CogIcon";
import { Survey } from "../Survey";
import { analytics } from "../../../../services/analytics";
import {
  selectPrograms,
  selectProgramsDict,
} from '../../../../features/program/programSlice';
import { getCognitoUserData } from '../../../../features/auth/authSlice';
import { selectUser } from '../../../../features/users/usersSlice';
import { selectGroup } from "../../../../features/groups/groupSlice";
import { selectCompany } from '../../../../features/company/companySlice';
import {
  fetchSurvey,
} from "../../../../features/evaluation/currentSurveySlice";
import { selectEvaluationById } from '../../../../features/evaluation/evaluationsSlice';
import { fetchHashByTemplateId, selectCertificateHash } from "../../../../features/students/certificateHashSlice";

import styles from "./lastPage.module.scss";
import { getTranslations } from "../../../../features/translations/translationsUtils";
import { fetchEvaluationResults, selectStudentEvaluationResult, selectStudentEvaluationResultIsFetching } from "../../../../features/students/evaluationsResultsSlice";
import { selectIsStudentExamResultFetching, selectStudentExamResult } from "../../../../features/students/examResultSlice";
import LoadingOverlay from "../../../common/LoadingOverlay";

interface LastPageProps {
  courseId: string;
  answersSelected: QuestionsAnswers;
  retakeExam: Function;
}

interface GetActionsParams {
  examWasPassed: boolean;
  retakeExam: Function;
  course: ICourse | null;
  courseId: string;
}

export const LastPage: FunctionComponent<LastPageProps> = ({
  courseId,
  answersSelected = {},
  retakeExam,
}) => {
  const [showSurvey, setShowSurvey] = useState<boolean>(false);
   const examResult = useSelector(selectStudentExamResult);
  const correctAnswersCount = examResult?.result ?? 0;
  const course = useSelector(selectStudentCourse);
  const evaluation = useSelector(selectEvaluation(course?.evaluation || ''));
  const surveyIsFetching = useSelector(selectStudentEvaluationResultIsFetching(course?.survey ?? ''));
  const surveyResults = useSelector(selectStudentEvaluationResult(course?.survey ?? ''));
  const isEvaluationResultsFetching = useSelector(selectIsStudentExamResultFetching);
  const isEvaluationResultRegistering = useSelector(isRegisteringEvaluationResult);
  
  const questionsCount = evaluation?.questions?.length || 1;
  const successRate = Math.round((correctAnswersCount / questionsCount) * 100);
  const examWasPassed = (examResult?.result ?? 0) >= (examResult?.correctAnswersToPass ?? 0);
  const title = examWasPassed ? getTranslations('STUDENT_EXAM_APPROVED') : getTranslations('STUDENT_EXAM_FAILED');
  const info = examWasPassed ? getTranslations('STUDENT_EXAM_DOWNLOAD_CERTIFICATE_INFO') : getTranslations('STUDENT_EXAM_FAILED_INFO');
  const icon = examWasPassed ? "Check-circle" : "Cross-circle";
  const iconColor = examWasPassed ? "#27AE60" : "#eb5757";
  const history = useHistory();
  const dispatch = useDispatch();

  const goToFinish = () => {
    history.push(`/course/${course?._id}/congratulations`);
  };
  const goToMyCourses = () => {
    history.push(`/myCourses`);
  };
  const selectSurveyRef = useRef<AnalyticsSelectSurveyRef>(null);
  const hash = useSelector(selectCertificateHash(course?.certificate || ""));
  const certificateId = course?.certificate;
  useEffect(() => {
    if (course?.survey) {
      dispatch(fetchEvaluationResults(course?.survey, course?._id, course?._id, ParentTypeEnum.CourseSurvey));
    }
  }, [course?._id, course?.survey, dispatch]);
  useEffect(() => {
    if (certificateId && !hash && examWasPassed) {
      setTimeout(() => {
        dispatch(fetchHashByTemplateId(certificateId));
      }, 2000)
    }
  }, [dispatch, certificateId, hash, examWasPassed]);

  const getActions = ({
    examWasPassed,
    retakeExam,
    course,
  }: GetActionsParams) => {
    const hasSurvey = course?.survey !== undefined;
    const buttonLabel = hasSurvey && !surveyResults ? getTranslations('STUDENT_EXAM_TAKE_SURVEY') : getTranslations('STUDENT_EXAM_COMPLETE_COURSE');
    if (!examWasPassed) {
      return [
        <div className={styles.buttonContainer}>
          <Button
            onClick={() => {
              if (hasSurvey) {
                selectSurveyRef?.current?.trigger();
                setShowSurvey(true);
                return;
              }
              goToMyCourses();
            }}
            type="link"
          >
            {getTranslations('STUDENT_EXAM_BACK_TO_COURSES')}
          </Button>
          <Button
            onClick={() => {
              retakeExam();
            }}
            type="primary"
          >
            {getTranslations('STUDENT_EXAM_RETRY')}
          </Button>
        </div>,
      ];
    }
    return <>
      <div className={styles.buttonContainer}>
        <Button
          disabled={surveyIsFetching}
          size="large"
          onClick={() => {
            if (hasSurvey && !surveyResults) {
              setShowSurvey(true);
              return;
            }
            goToFinish();
          }}
          type="primary"
        >
          {buttonLabel}
        </Button>
      </div >,
    </>;
  };

  const questionsSummary = !evaluation || examWasPassed ? [] :
    evaluation?.questions.map(question => {
      return {
        key: question._id,
        title: question.title,
        correct: answersSelected[question?._id ?? '']?.correct ?? false,
      }
    });

  if (isEvaluationResultsFetching || isEvaluationResultRegistering) {
    return <LoadingOverlay spinning={true} />;
  }

  return (
    <Card
      className={styles.container}
    >
      {showSurvey ? (
        <Survey
          courseId={courseId}
          parentId={courseId}
          surveyId={course?.survey || ""}
          onSurveyComplete={() => {
            goToFinish();
          }}
          parentType={ParentTypeEnum.CourseSurvey}
          onCancelSurvey={() => setShowSurvey(false)}
        />
      ) : null}
      <CogIcon color={iconColor} className={styles.icon} icon={icon} />
      <div className={styles.title}>{title}</div>
      <div className={styles.info}>{info}</div>
      <div className={styles.results}>
        <div className={styles.result}>
        {getTranslations('STUDENT_EXAM_SCORE', [`${correctAnswersCount} / ${questionsCount}`])}
        </div>
        <div className={styles.result}>
        {getTranslations('STUDENT_EXAM_RIGHT_ANSWERS_PERCENT', [successRate])}
        </div>
        <div className={styles.result}>
        {getTranslations('STUDENT_EXAM_RIGHT_ANSWERS_COUNT', [correctAnswersCount])}
        </div>
        <div className={styles.result}>
        {getTranslations('STUDENT_EXAM_WRONG_ANSWERS_COUNT', [questionsCount - correctAnswersCount])}
        </div>
        {!examWasPassed ? (
          <div className={styles.result}>
            {getTranslations('STUDENT_EXAM_RETRY_IN', [course?.timeToRetry ?? 0])}
          </div>
        ) : null}
      </div>
      {questionsSummary.length === 0 ? null :
        <div className={styles.questions}>
          <div className={styles.title}>{getTranslations('STUDENT_EXAM_ANSWERS_DETAILS')}</div>
          {questionsSummary.map(question => {
            const { correct, key, title } = question;
            return <div key={key} className={cls(styles.question, { [styles.failed]: !question.correct })}>
              <CogIcon color={correct ? '#27AE60' : '#EB5757'} className={styles.icon} icon={correct ? 'Check' : 'Cross'} /> {title}
            </div>
          })}
        </div>}
      {getActions({
        examWasPassed,
        retakeExam,
        course,
        courseId,
      })}
      <AnalyticsSelectSurvey ref={selectSurveyRef} parentId={courseId} surveyId={course?.survey || ""} />
    </Card>
  );
};

type AnalyticsSelectSurveyRef = {
  trigger: () => void,
}

interface AnalyticsSelectSurveyProps {
  parentId: string,
  surveyId: string,
}

export const AnalyticsSelectSurvey =
  forwardRef<AnalyticsSelectSurveyRef, AnalyticsSelectSurveyProps>(({ parentId, surveyId }, ref) => {
    useImperativeHandle(ref, () => ({
      trigger: () => {
        analytics.selectSurvey({
          survey: evaluation,
          course: course,
          program: courseProgram,
          company: company,
          group: group || null,
        });
      }
    }));
    const dispatch = useDispatch();
    const evaluation = useSelector(selectEvaluationById(surveyId));
    const course = useSelector(selectStudentCourse);
    const allProgramsIds = useSelector(selectPrograms);
    const programsDict = useSelector(selectProgramsDict);
    const programId = allProgramsIds.filter(programId => {
      const program = programsDict[programId] || {};
      const { courses: programCourses = [] } = program;
      const foundCourse = programCourses.find(courseId => courseId === course?._id);
      if (foundCourse !== undefined) {
        return true;
      }
      return false;
    })
    const courseProgram = programsDict[programId[0]] || null;
    const cognitoUserData = useSelector(getCognitoUserData);
    const cognitoUsername = cognitoUserData
      ? cognitoUserData["cognito:username"]
      : null;
    const userFromStore = useSelector(selectUser(cognitoUsername));
    const group = useSelector(selectGroup(userFromStore?.studentGroupId || '')) || null;
    const companyId = userFromStore?.company ?? "";
    const company = useSelector(
      selectCompany(companyId)
    );

    useEffect(() => {
      if (parentId && surveyId) {
        dispatch(fetchSurvey(surveyId, parentId));
      }
    }, [dispatch, parentId, surveyId]);

    return null;
  });
